/**
 * useWeeklyPlan — fetches GET /me/weekly-plan and exposes plan/loading/error state,
 * plus a generate() mutation that POSTs to POST /me/weekly-plan then refreshes.
 *
 * Design mirrors useTracking:
 *   - getIdToken() from AuthContext — NEVER stores the token in state.
 *   - Fetches once on mount.
 *   - Loading starts true; becomes false on first load (success or failure).
 *   - plan is null on empty state (no plan generated yet) or error.
 *   - generate() sets generating=true, POSTs, then re-fetches on success.
 *   - uid ALWAYS from verified token — never sent in request body.
 *   - No PII is logged.
 *   - No dangerouslySetInnerHTML usage.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { WeeklyPlan } from "@autodieta/shared";
import { useAuth } from "../auth/AuthContext.js";

// ---------------------------------------------------------------------------
// Response type from GET /me/weekly-plan
// ---------------------------------------------------------------------------

interface WeeklyPlanResponse {
  weeklyPlan: WeeklyPlan | null;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseWeeklyPlanResult {
  /** True while the initial fetch (or a refresh triggered by generate()) is in progress. */
  loading: boolean;
  /** Error message if the last fetch failed. Undefined on success. */
  error?: string;
  /** The user's current WeeklyPlan, or null if none has been generated. */
  plan: WeeklyPlan | null;
  /** True while generate() POST is in-flight. */
  generating: boolean;
  /** Error message if generate() failed. Undefined on success or before first call. */
  generateError?: string;
  /** Trigger POST /me/weekly-plan; refreshes plan on success. */
  generate: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// useWeeklyPlan
// ---------------------------------------------------------------------------

/**
 * Fetches the weekly plan from the worker and provides a generate() mutation
 * that POSTs to POST /me/weekly-plan, then re-fetches on success.
 *
 * @param workerUrl - Base URL of the Cloudflare Worker.
 */
export function useWeeklyPlan(workerUrl: string): UseWeeklyPlanResult {
  const { getIdToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [plan, setPlan] = useState<WeeklyPlan | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | undefined>(undefined);

  const workerUrlRef = useRef(workerUrl);
  workerUrlRef.current = workerUrl;

  const fetchPlan = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${workerUrlRef.current}/me/weekly-plan`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const userMessage =
          res.status === 401
            ? "Tu sesión expiró. Por favor, volvé a iniciar sesión."
            : "No se pudo cargar el plan semanal. Intentá de nuevo más tarde.";
        setError(userMessage);
        setPlan(null);
      } else {
        const body = (await res.json()) as WeeklyPlanResponse;
        setPlan(body.weeklyPlan);
        setError(undefined);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      setError(message);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    void fetchPlan();
  }, [fetchPlan]);

  /**
   * generate — POSTs to POST /me/weekly-plan with the Firebase ID token.
   * uid is derived server-side from the token — NEVER sent in the request body.
   * On success, re-fetches the plan (GET) to keep the view in sync.
   */
  const generate = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      setGenerateError("Tu sesión expiró. Por favor, volvé a iniciar sesión.");
      return;
    }

    setGenerating(true);
    setGenerateError(undefined);

    try {
      const res = await fetch(`${workerUrlRef.current}/me/weekly-plan`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        // Body intentionally empty object — uid comes from the verified token server-side.
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const userMessage =
          res.status === 401
            ? "Tu sesión expiró. Por favor, volvé a iniciar sesión."
            : res.status === 404
              ? "Completá tu perfil antes de generar el plan semanal."
              : "No se pudo generar el plan semanal. Intentá de nuevo más tarde.";
        setGenerateError(userMessage);
        return;
      }

      // Success — refresh the plan from the server
      setLoading(true);
      await fetchPlan();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      setGenerateError(message);
    } finally {
      setGenerating(false);
    }
  }, [getIdToken, fetchPlan]);

  return {
    loading,
    plan,
    generating,
    generate,
    ...(error !== undefined ? { error } : {}),
    ...(generateError !== undefined ? { generateError } : {}),
  };
}
